import type { LandingContent } from "./types";

export const landingEn: LandingContent = {
  locale: "en",
  seo: {
    title: "Luxoria - HDR Conversion for Photos and Videos",
    description:
      "Luxoria is a lightweight HDR conversion app for photos and videos. Convert SDR media into HDR-ready outputs with a clean, focused workflow."
  },
  nav: {
    features: "Features",
    workflow: "Workflow",
    privacy: "Privacy",
    terms: "Terms",
    support: "Support",
    language: "中文",
    menu: "Menu"
  },
  hero: {
    eyebrow: "SDR to HDR, made simple",
    title: "Bring SDR photos and videos into HDR.",
    subtitle:
      "Luxoria is a lightweight HDR conversion app for photos and videos. Convert everyday SDR media into HDR-ready outputs with a clean, focused workflow.",
    tags: ["Photo HDR", "Video HDR", "Batch Conversion", "Local-first"],
    primaryCta: "Download on the App Store",
    secondaryCta: "View features"
  },
  value: {
    title: "Made for modern HDR displays.",
    body: "Many photos and videos are still SDR. On HDR-capable displays, highlights, skies, lights, and bright scenes can feel limited. Luxoria helps create HDR-ready versions while keeping the conversion flow simple.",
    cards: [
      {
        icon: "sun",
        title: "More expressive highlights",
        body: "Recover detail in skies, lights and bright scenes for HDR screens."
      },
      {
        icon: "wand",
        title: "A cleaner conversion workflow",
        body: "A focused flow: choose, convert, review, and save without clutter."
      },
      {
        icon: "layers",
        title: "For both photos and videos",
        body: "One app for HDR HEIC photos and HEVC Main10 HLG video output."
      }
    ]
  },
  features: {
    eyebrow: "Core Features",
    title: "What Luxoria does.",
    cards: [
      {
        icon: "image",
        title: "Photo HDR Conversion",
        body: "Convert SDR photos into HDR HEIC outputs with HDR metadata and verification.",
        bullets: ["Single photo workflow", "HDR preview", "Adjustable conversion strength", "HDR HEIC output"]
      },
      {
        icon: "copy",
        title: "Batch Photo Conversion",
        body: "Process multiple photos in one focused flow, with progress and result summaries.",
        bullets: ["Multiple image selection", "Progress tracking", "Partial result handling", "Save-ready outputs"]
      },
      {
        icon: "video",
        title: "Video HDR Conversion",
        body: "Convert SDR videos into HDR-ready HEVC Main10 HLG outputs with audio passthrough.",
        bullets: ["Source video analysis", "Frame-level HDR processing", "HEVC Main10 HLG export", "Result verification"]
      },
      {
        icon: "share-2",
        title: "Save and Share",
        body: "Export converted media to Photos, Files, or a selected folder depending on your platform.",
        bullets: ["Save to Photos", "Export to Files", "Share outputs", "Show in Finder on macOS"]
      }
    ]
  },
  workflow: {
    title: "A focused HDR conversion flow.",
    steps: [
      {
        num: "01",
        icon: "images",
        title: "Choose your media",
        body: "Select one photo, multiple photos, or a video."
      },
      {
        num: "02",
        icon: "wand",
        title: "Convert to HDR",
        body: "Luxoria analyzes and processes the source with an HDR-focused pipeline."
      },
      {
        num: "03",
        icon: "save",
        title: "Save the result",
        body: "Export the converted photo or video to Photos, Files, or your Mac."
      }
    ]
  },
  gallery: {
    title: "Built for a simple conversion experience.",
    body: "Luxoria keeps the main flow simple: choose media, convert, review, and save.",
    shots: [
      { label: "Home", image: "/images/app-store/screen-1.png" },
      { label: "Photo HDR", image: "/images/app-store/screen-2.png" },
      { label: "Batch Photos", image: "/images/app-store/screen-3.png" },
      { label: "Video HDR", image: "/images/app-store/screen-4.png" }
    ]
  },
  privacy: {
    title: "We take your privacy seriously.",
    body: "Privacy is a priority for Luxoria. Conversions run on your device, the photos and videos you choose are only used to create the converted output, and temporary files are cleaned up after use.",
    tags: ["On-device processing", "Temporary files cleaned up", "Only the media you select"],
    linkText: "Read the Privacy Policy"
  },
  footer: {
    tagline: "A lightweight HDR conversion app for photos and videos.",
    productBy: "An Axqora product.",
    productHeading: "Product",
    featuresLabel: "Features",
    workflowLabel: "Workflow",
    supportLabel: "Support",
    legalHeading: "Legal",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    contactHeading: "Contact us",
    rights: "© 2026 Axqora. All rights reserved.",
    icp: "粤ICP备2021054614号",
    icpHref: "https://beian.miit.gov.cn/"
  }
};
